import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScanner } from '@/root/App/com/ScannerProvider/ScannerProvider';

/**
 * Opens a screen's zone scanner as the screen loads, for screens where the zone is the only
 * thing the operator has to give — on every device whose scanner can start (desktop included),
 * and only while the zone is empty.
 *
 * Once per visit, and a zone already set — a purchase order's default zone, say — never opens it.
 * The X on the scanner that opened with the screen leaves the screen: the operator arrived to
 * scan a zone and has decided not to, so they go back to the page they came from (home, if they
 * arrived from outside the app) — never to the list. That holds for the whole of that first
 * opening, including after a detour to the list and "Back to the scanner": the picker never closed,
 * so the screen behind it was never shown. Once that opening is over (a zone chosen, or the list
 * closed), the picker opens only on a tap, and its X cancels back to the screen as usual.
 *
 * Returns the prompt for the empty zone card, which says what a tap does on this device, and
 * `pickerProps(cancel)`: the `onClose` / `onScannerClose` pair for the screen's ZonePickerModal,
 * given the screen's own "close the picker".
 */
export function useZoneScanOnLoad(open: () => void, zoneEmpty: boolean) {
  const { scanFirst } = useScanner();
  const navigate = useNavigate();
  // React Router gives the first location of a visit the key "default": nothing in the app to go
  // back to.
  const hasPreviousPage = useLocation().key !== 'default';
  const openRef = useRef(open);
  openRef.current = open;
  // True while the picker that opened with the screen is still open.
  const openedOnLoadRef = useRef(false);

  useEffect(() => {
    if (!scanFirst || !zoneEmpty) return;
    openedOnLoadRef.current = true;
    openRef.current();
    // The screen's first render only — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    tapPrompt: scanFirst ? 'Tap to scan the zone label' : 'Tap to choose a zone',
    pickerProps: (cancel: () => void) => ({
      // A zone was chosen or the list closed: the opening is over.
      onClose: () => {
        openedOnLoadRef.current = false;
        cancel();
      },
      // The scanner's X: leave the screen if this scanner opened with it.
      onScannerClose: () => {
        if (!openedOnLoadRef.current) {
          cancel();
          return;
        }
        openedOnLoadRef.current = false;
        if (hasPreviousPage) navigate(-1);
        else navigate('/');
      }
    })
  };
}

/**
 * "12 × Advil 200mg", "12 × Advil 200mg and 2 more products", or null when nothing is chosen —
 * the unserialized screens' quantities, named from the zone's stock records they refer to.
 */
export const describeUnits = (
  amounts: Array<{ id: string; quantity?: number | null }>,
  balances: Array<{ id: string; product?: { title?: string | null } | null }>
): string | null => {
  const chosen = amounts.filter(({ quantity }) => (quantity || 0) > 0);
  if (!chosen.length) return null;
  const [first, ...rest] = chosen;
  const product = balances.find(({ id }) => id === first.id)?.product;
  const title = product?.title?.trim() || 'product';
  const more = rest.length
    ? ` and ${rest.length} more product${rest.length === 1 ? '' : 's'}`
    : '';
  return `${first.quantity} × ${title}${more}`;
};

export default useZoneScanOnLoad;
